import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** tiny helper: infer MIME from filename if server doesn't send Content-Type */
function inferMimeFromName(name = "") {
    const ext = (name.split(".").pop() || "").toLowerCase();
    if (ext === "pdf") return "application/pdf";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext)) return `image/${ext === "jpg" ? "jpeg" : ext}`;
    if (ext === "txt") return "text/plain";
    return "application/octet-stream";
}


export default function usePreview(options = {}) {
    const { getToken = () => localStorage.getItem("token"), fetcher = fetch } = options;

    const [open, setOpen] = useState(false);
    const [url, setUrl] = useState("");
    const [mime, setMime] = useState("");
    const [name, setName] = useState("document");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const lastUrlRef = useRef(""); // for cleanup
    const abortRef = useRef(null);

    // revoke old blob url when url changes
    useEffect(() => {
        if (lastUrlRef.current && lastUrlRef.current !== url) {
            try { URL.revokeObjectURL(lastUrlRef.current); } catch {}
        }
        lastUrlRef.current = url;
    }, [url]);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort();
            if (lastUrlRef.current) {
                try { URL.revokeObjectURL(lastUrlRef.current); } catch {}
            }
        };
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        setError("");
        // keep url until dialog fully closes; optional: revoke immediately
    }, []);


    const openPreview = useCallback(
        async (docOrUrl, opts = {}) => {
            const { rawUrl = false, apiBuilder } = opts;
            setError("");
            setBusy(true);

            // cancel any in-flight
            if (abortRef.current) abortRef.current.abort();
            const ac = new AbortController();
            abortRef.current = ac;

            try {
                let blobUrl = "";
                let contentType = "";
                let fileName = "";

                if (typeof docOrUrl === "string") {
                    if (rawUrl) {
                        // direct URL (public or already signed)
                        setMime("");
                        setName("document");
                        setUrl(docOrUrl);
                        setOpen(true);
                        return;
                    }
                    throw new Error("A string was provided, but rawUrl=false. Provide a doc object or set rawUrl=true.");
                }

                const { _id, id, originalName, fileName: fname } = docOrUrl || {};
                const docId = _id || id;
                if (!docId && !rawUrl) throw new Error("Missing document id.");

                // build request URL
                const requestUrl =
                    (apiBuilder && apiBuilder(docOrUrl)) || `/api/uploads/document/${encodeURIComponent(docId)}`;

                const headers = {};
                const token = getToken?.();
                if (token) headers.Authorization = `Bearer ${token}`;

                const res = await fetcher(requestUrl, { method: "GET", headers, signal: ac.signal });
                if (!res.ok) {
                    let msg = `HTTP ${res.status}`;
                    try {
                        const payload = await res.json();
                        if (payload?.error) msg = payload.error;
                    } catch {}
                    throw new Error(`Aperçu: ${msg}`);
                }

                // filename from Content-Disposition or fallback to originalName/fileName
                const cd = res.headers.get("Content-Disposition") || "";
                const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                fileName = m ? decodeURIComponent(m[1].replace(/['"]/g, "")) : (originalName || fname || "document");

                // mime from server or infer
                contentType = res.headers.get("Content-Type") || inferMimeFromName(fileName);

                const blob = await res.blob();
                blobUrl = URL.createObjectURL(blob);

                setName(fileName);
                setMime(contentType);
                setUrl(blobUrl);
                setOpen(true);
            } catch (e) {
                if (e.name !== "AbortError") setError(e.message || "Erreur d’aperçu");
            } finally {
                setBusy(false);
            }
        },
        [fetcher, getToken]
    );

    const canInline = useMemo(() => {
        if (!mime) return false;
        if (mime.includes("pdf")) return true;
        if (mime.startsWith("image/")) return true;
        return false;
    }, [mime]);

    return {
        // state
        open,
        url,
        mime,
        name,
        busy,
        error,

        // actions
        openPreview,
        close,

        // helpers
        canInline,
    };
}
